import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Typography, Flex } from "antd"
import { RobotOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { marked } from "marked"
import { GenericChartWidget } from "../../widgets"
import AddChartPrompt from "../AddChartPrompt/AddChartPrompt"
import styles from "../../style.module.css"

const { Text } = Typography

export default function ChatMessage({ message, onChartDismiss, onChartOptionSelect, onOptionSelect }) {
  const { t } = useTranslation()
  const renderedText = useMemo(() => ({ __html: marked.parse(message.text ?? "") }), [message.text])

  const isUser = message.sender === "user"

  return (
    <Flex gap={8} align="flex-start" style={isUser ? { flexDirection: "row-reverse" } : undefined} className={styles.chatMsg}>
      {message.sender === "ai" && (
        <Flex align="center" justify="center" className={styles.dashAvatarSm}>
          <RobotOutlined className={styles.chatAvatarIconSm} />
        </Flex>
      )}
      <Flex vertical align={isUser ? "flex-end" : "flex-start"} className={styles.chatMsgInner}>
        <div
          className={`${styles.chatBubble} ${message.sender === "user" ? styles.chatBubbleUser : styles.chatBubbleAi}`}
          dangerouslySetInnerHTML={renderedText}
        />
        {message.options?.items?.length > 0 && (
          <div className={styles.chatOptionTiles}>
            {message.options.items.map((opt) => {
              const isSelected = message.selectedOption === opt.value
              const isDisabled = message.selectedOption != null
              return (
                <button
                  key={opt.value}
                  className={`${styles.chatOptionTile} ${isSelected ? styles.chatOptionTileSelected : ""} ${isDisabled ? styles.chatOptionTileDisabled : ""}`}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && onOptionSelect?.(message.id, opt)}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        )}
        {message.chartPayload && (
          <>
            {message.chartPayload.chart_data != null && (
              <div className={styles.chatChartPreview}>
                <GenericChartWidget data={message.chartPayload} />
              </div>
            )}
            {message.chartPromptDismissed ? (
              message.chartSelectedOption && (
                <div className={styles.chatChartPrompt}>
                  <Text type="secondary" className={styles.textSm}>{message.chartPayload?.options?.label ?? t("ai_dashboard.add_chart_prompt")}</Text>
                  <div><Text strong className={styles.textSm}>✓ {message.chartSelectedOption}</Text></div>
                </div>
              )
            ) : (
              <AddChartPrompt
                payload={message.chartPayload}
                onDismiss={(label) => onChartDismiss(message.id, label)}
                onOptionSelect={onChartOptionSelect}
              />
            )}
          </>
        )}
        {message.widgetSaved && (
          <Text type="secondary" className={styles.textSm}>✓ {t("ai_dashboard.chart_saved")}</Text>
        )}
        {message.actions && (
          <Flex wrap gap={6} className={styles.chatActions}>
            {message.actions.map((a) => (
              <Button key={a} size="small" className={styles.chatActionBtn}>{a}</Button>
            ))}
          </Flex>
        )}
        <Text type="secondary" className={styles.chatMsgTime}>{message.time}</Text>
      </Flex>
    </Flex>
  )
}

ChatMessage.propTypes = {
  message: PropTypes.object.isRequired,
  onChartDismiss: PropTypes.func.isRequired,
  onChartOptionSelect: PropTypes.func,
  onOptionSelect: PropTypes.func,
}
